import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { signInWithGoogle } from "@/utils/auth";

const TermsOfService = () => {
  const navigate = useNavigate();

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Terms of Service - Seoro"
        description="Read Seoro's Terms of Service. Understand your rights and responsibilities when using our AI-powered SEO content automation platform."
        keywords="terms of service, terms and conditions, Seoro terms, legal, user agreement"
      />
      <Header onSignIn={handleSignIn} />
      
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="space-y-8">
          <div className="text-center space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground">
              Terms of Service
            </h1>
            <p className="text-muted-foreground">
              Last updated: January 2025
            </p>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Please read these Terms of Use ("Terms") carefully before accessing or using Seoro.so (the "Service"). 
              By accessing or using any part of the Service, you agree to be legally bound by these Terms.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8 text-muted-foreground">
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">1. Definitions</h2>
              <div className="space-y-2 leading-relaxed">
                <p><strong className="text-foreground">"Seoro," "we," "us," and "our"</strong> mean the operator(s) of the Service, together with all parents, subsidiaries, affiliates, successors, and assigns.</p>
                <p><strong className="text-foreground">"Content"</strong> means all text, graphics, data, software, audio, video, information, and other materials available on or through the Service.</p>
                <p><strong className="text-foreground">"User Contribution"</strong> means any Content that a User submits, posts, publishes, displays, or otherwise makes available on or through the Service, including but not limited to website URLs, article briefs, brand information, target keywords, and other inputs provided for content generation.</p>
                <p><strong className="text-foreground">"Generated Content"</strong> means articles, images, text, and other materials created by our AI-powered systems based on your User Contributions.</p>
                <p><strong className="text-foreground">"Backlink Service"</strong> means the portion of the Service that enables Users to receive premium backlinks (Domain Authority 70+) placed on third-party websites.</p>
                <p><strong className="text-foreground">"CMS Integration"</strong> means the Service's ability to publish Generated Content directly to third-party content management systems, including but not limited to WordPress, Shopify, and Webflow.</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">2. Changes to Terms & Service</h2>
              <p className="leading-relaxed">
                We may revise these Terms—and may change, suspend, or discontinue any part of the Service—at any time in our sole discretion. 
                If we make material changes, we will post the revised Terms and provide at least thirty (30) days' notice before they take effect. 
                Your continued use of the Service after the effective date constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">3. Eligibility & Compliance</h2>
              <p className="leading-relaxed">
                You represent that you (a) are at least 18 years old and legally capable of entering into contracts; 
                (b) have not been previously suspended or removed from the Service; and (c) will comply with all applicable laws, 
                regulations, and third-party terms (including search-engine policies, CMS platform terms, and content licensing requirements) 
                when using the Service.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">4. User Accounts</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.1 Account Creation</h3>
                  <p>To use Seoro, users must create an account by providing accurate and complete information, including their name, email address, and any other required information. You may use Google OAuth or other authentication methods we provide.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.2 Account Security</h3>
                  <p>Users are responsible for maintaining the confidentiality of their account credentials and must report unauthorized access immediately. Account details must be kept up to date. You are responsible for all activities that occur under your account.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.3 Account Liability</h3>
                  <p>Seoro is not liable for any loss or damage arising from unauthorized account use. There are no age restrictions for using Seoro; however, users under the age of majority in their jurisdiction should obtain parental consent before registering.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">4.4 Account Deletion</h3>
                  <p>Users may delete their accounts directly through the platform, and all user data will be removed as outlined in our Privacy Policy. Upon account deletion, your access to Generated Content and Service features will terminate, subject to the survival provisions in Section 15.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">5. Intellectual Property</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">5.1 Ownership</h3>
                  <p>All intellectual property rights, including but not limited to platform features, algorithms, AI models, designs, trademarks, and the Service itself, remain the exclusive property of Seoro and its licensors. The Service and all Content (excluding User Contributions and Generated Content) are protected by intellectual-property laws.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">5.2 Limited License</h3>
                  <p>Users are granted a limited, non-exclusive, non-transferable, revocable license to use the platform in accordance with these Terms for your internal business purposes. No other rights are granted.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">5.3 Restrictions</h3>
                  <p>Users may not reverse-engineer, decompile, reproduce, redistribute, or create derivative works from any aspect of the platform without prior written consent from Seoro. You may not use automated means (including bots or scripts) to access the Service except through documented APIs we expressly make available.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">6. User Submissions & Generated Content</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.1 Submission Rights & Responsibility</h3>
                  <p>Users may provide materials such as website URLs, article briefs, brand information, target keywords, or other inputs to generate content through Seoro. By submitting such materials, users confirm that they have the necessary rights to share and authorize Seoro to access and use this data for the purpose of generating content and providing the Service.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.2 User Ownership of Generated Content</h3>
                  <p>You retain ownership of Generated Content created for your account. However, you acknowledge that Generated Content is created using AI technology and may be similar to content generated for other users. You are solely responsible for reviewing, editing, and ensuring Generated Content meets your needs and complies with applicable laws before publishing.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.3 License to Seoro</h3>
                  <p>By submitting any User Contribution, you grant us a worldwide, royalty-free, perpetual, irrevocable, sublicensable, transferable license to use, host, store, reproduce, modify, create derivative works from, publish, display, and distribute that User Contribution in connection with operating and improving the Service.</p>
                  <p className="mt-2">Additionally, by using the Service, you authorize Seoro to display your company logo and name as a customer reference on our marketing website or landing pages, unless you explicitly opt out by contacting us at <a href="mailto:support@seoro.so" className="text-primary hover:underline">support@seoro.so</a>.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">6.4 AI-Generated Content Disclaimer</h3>
                  <p>You acknowledge that Generated Content is created using artificial intelligence and machine learning technologies. While we strive for quality and accuracy, Generated Content may contain errors, inaccuracies, or may not meet your specific requirements. You are responsible for reviewing, fact-checking, and editing all Generated Content before publication. Seoro does not guarantee that Generated Content will be error-free, accurate, or suitable for your intended use.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">7. Service Features & Limitations</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.1 Content Generation</h3>
                  <p>Seoro provides AI-powered content generation services, including but not limited to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>SEO-optimized article generation (up to 30 articles per month based on your subscription)</li>
                    <li>Topic cluster and article ideation</li>
                    <li>AI-generated images in multiple styles</li>
                    <li>Multi-language content generation (76+ languages)</li>
                    <li>LLM.txt file generation</li>
                  </ul>
                  <p className="mt-2">You acknowledge that content generation is subject to usage limits based on your subscription plan. Unused credits do not roll over to subsequent billing periods unless explicitly stated.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.2 Website Analysis</h3>
                  <p>Seoro provides website analysis services (1 comprehensive analysis per month based on your subscription) that may include competitor analysis, content gap identification, and SEO recommendations. Analysis results are provided for informational purposes and do not guarantee specific outcomes or improvements.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.3 Backlink Service</h3>
                  <p>When you activate or redeem backlink credits, our system automatically places backlinks on third-party websites with Domain Authority 70 or higher. Before activating backlinks, ensure those placements align with your own policies, brand, and risk tolerance. We cannot guarantee:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>The safety, reputation, or compliance of any third-party site where backlinks are placed</li>
                    <li>That a backlink will improve your search rankings, domain authority, or traffic</li>
                    <li>That search-engine algorithms or policies will remain unchanged</li>
                    <li>The permanence of backlink placements (third-party sites may remove links)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.4 Search-Engine Guidelines</h3>
                  <p>Search engines set and update their own rules. They may reduce visibility or penalize sites they believe use links in ways that violate those rules. By choosing to use the Backlink Service, you accept that risk and agree to indemnify and hold us harmless from any related penalties, losses, or liabilities.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.5 CMS Integration</h3>
                  <p>Seoro provides integration with third-party CMS platforms (WordPress, Shopify, Webflow, and others). You are responsible for:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                    <li>Maintaining valid credentials and access to your CMS accounts</li>
                    <li>Ensuring you have the right to publish content to connected platforms</li>
                    <li>Complying with all terms of service of third-party CMS platforms</li>
                    <li>Verifying that published content appears correctly on your website</li>
                  </ul>
                  <p className="mt-2">Seoro is not responsible for errors, formatting issues, or failures in third-party CMS platforms. We do not guarantee that CMS integrations will be uninterrupted or error-free.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">7.6 Google Search Console Integration</h3>
                  <p>If you connect your Google Search Console account, Seoro may access and display your search performance data. You are responsible for maintaining the security of your Google account credentials and complying with Google's terms of service. Seoro is not responsible for changes to Google Search Console API or data availability.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">8. Acceptable Use & Prohibited Conduct</h2>
              <p className="leading-relaxed mb-2">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the Service for any illegal, deceptive, or fraudulent purpose</li>
                <li>Interfere with, disrupt, or impair the functioning of the Service</li>
                <li>Copy, scrape, harvest, or index any Content without authorization</li>
                <li>Attempt to gain unauthorized access to any accounts, systems, or networks</li>
                <li>Use automated means (including bots or scripts) to access the Service except through documented APIs we expressly make available</li>
                <li>Generate content that violates intellectual property rights, including using the Service to create content that infringes on copyrighted material</li>
                <li>Use Generated Content in a manner that violates applicable laws or third-party rights</li>
                <li>Resell, redistribute, or sublicense Generated Content to third parties without our prior written consent</li>
                <li>Use the Service to generate content for competitors of Seoro or to create competing services</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">9. Fees, Payment & Refunds</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">9.1 Fees</h3>
                  <p>Prices for subscriptions are as listed on the Service and are subject to change at any time before purchase. Current pricing is $99 per month for the All-in-One Plan, which includes 30 article generations, 10 premium backlinks, and 1 website analysis per month.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">9.2 Payment Processor</h3>
                  <p>Payments are processed by third-party payment providers (including but not limited to Stripe, Polar, or other providers). We are not responsible for their acts or omissions. You agree to provide current, complete, and accurate billing information.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">9.3 Subscription Renewal</h3>
                  <p>Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date. You authorize us to charge your payment method for renewal fees.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">9.4 Refund Policy</h3>
                  <p className="mb-3">First-time subscribers may request a full refund within thirty (30) calendar days of the initial subscription payment by contacting us at <a href="mailto:support@seoro.so" className="text-primary hover:underline">support@seoro.so</a>, subject to the following conditions:</p>
                  
                  <p className="font-semibold text-foreground mb-2 mt-4">Refunds are NOT available if:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>You have claimed, redeemed, or activated any backlinks through the Service (backlinks incur costs to us and cannot be reversed)</li>
                    <li>You have published 10 or more articles to a third-party CMS platform</li>
                    <li>You have exceeded 10 article generations</li>
                    <li>The 30-day refund period has expired</li>
                  </ul>

                  <p className="font-semibold text-foreground mb-2 mt-4">Refund Eligibility:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Refunds are limited to one per customer</li>
                    <li>Refunds will be issued to the original payment method</li>
                    <li>After the 30-day period, all sales are final and non-refundable</li>
                    <li>Refunds will not be provided for partial months of service or unused content credits</li>
                    <li>If you have used any paid features (backlinks, CMS publishing, or substantial article generation), you may be eligible for a prorated refund at our sole discretion, minus the cost of services already rendered</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">10. Modifications, Suspension & Termination</h2>
              <p className="leading-relaxed">
                We may modify, suspend, or terminate the Service—or your access to it—at any time, with or without notice, 
                for any reason, including if we believe you have violated these Terms. Upon termination:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Your license to use the Service will automatically terminate</li>
                <li>You will lose access to Generated Content stored on our platform</li>
                <li>Sections 5, 6, 8, 11–17 will survive termination</li>
                <li>We are not obligated to provide refunds for any remaining subscription period</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">11. Disclaimer of Warranties</h2>
              <p className="leading-relaxed mb-2">
                The Service is provided "as is" and "as available." To the maximum extent permitted by law, Seoro disclaims 
                all warranties, express or implied, including any implied warranties of merchantability, fitness for a 
                particular purpose, non-infringement, and results. Without limiting the foregoing, we make no warranty that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                <li>Any Content (including User Contributions, Generated Content, or backlinks) is accurate, reliable, compliant with search-engine guidelines, or free from errors</li>
                <li>The Service will improve search rankings, domain authority, traffic, or revenue</li>
                <li>Search-engine algorithms or policies will remain unchanged</li>
                <li>Generated Content will be unique or free from similarities to other content</li>
                <li>AI-generated images will be free from copyright issues or third-party claims</li>
                <li>CMS integrations will function without errors or interruptions</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">12. Limitation of Liability</h2>
              <p className="leading-relaxed">
                To the maximum extent permitted by law, in no event will Seoro, its affiliates, licensors, or service 
                providers be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, 
                or for any lost profits, lost revenue, lost data, business interruption, or cost of substitute services, 
                arising out of or in connection with the Service or these Terms—even if advised of the possibility of such damages.
              </p>
              <p className="mt-2">
                Our total cumulative liability for any claims relating to the Service will not exceed the amount you paid us, 
                if any, for the Service in the twelve (12) months before the event giving rise to liability.
              </p>
              <p className="mt-2">This limitation applies to all claims, including but not limited to claims related to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
                <li>Generated Content quality, accuracy, or suitability</li>
                <li>Backlink placements and their effects on search rankings</li>
                <li>CMS integration failures or errors</li>
                <li>Data loss or corruption</li>
                <li>Service interruptions or unavailability</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">13. Indemnification</h2>
              <p className="leading-relaxed">
                You will defend, indemnify, and hold harmless Seoro and its officers, directors, employees, agents, and affiliates 
                from and against any claims, damages, liabilities, losses, costs, or expenses (including reasonable attorneys' fees) 
                arising out of or relating to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Your use of the Service</li>
                <li>Your User Contributions</li>
                <li>Your publication or use of Generated Content</li>
                <li>Your violation of these Terms or any laws</li>
                <li>Your interactions with third-party websites or services (including backlink transactions, CMS platforms, and Google Search Console)</li>
                <li>Any claim that Generated Content or your use of the Service infringes on third-party intellectual property rights</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">14. Governing Law & Venue</h2>
              <p className="leading-relaxed">
                These Terms and any dispute arising from or related to them will be governed by and construed in accordance 
                with the laws of the jurisdiction where Seoro operates, without regard to its conflict-of-law principles. 
                The courts located in that jurisdiction will have exclusive jurisdiction over all disputes not subject to arbitration.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">15. Dispute Resolution & Arbitration</h2>
              <div className="space-y-3 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">15.1 Informal Negotiations</h3>
                  <p>Before commencing formal proceedings, you agree to first contact us at <a href="mailto:support@seoro.so" className="text-primary hover:underline">support@seoro.so</a> and attempt to resolve the dispute informally for at least thirty (30) days.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">15.2 Binding Arbitration</h3>
                  <p>If we cannot resolve the dispute informally, any controversy or claim arising out of or relating to the Service or these Terms shall be settled by binding arbitration administered by a recognized arbitration provider, under its rules then in effect. The arbitration will be conducted in English by a single arbitrator.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">15.3 Class-Action Waiver</h3>
                  <p>You may only resolve disputes with us on an individual basis, and may not bring a claim as a plaintiff or class member in any purported class or representative proceeding.</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">16. Severability & Waiver</h2>
              <p className="leading-relaxed">
                If any provision of these Terms is held to be invalid or unenforceable, that provision will be enforced to 
                the maximum extent permissible and the remaining provisions will remain in full force and effect. No waiver 
                of any term shall be deemed a further or continuing waiver of that term or any other term.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">17. Entire Agreement</h2>
              <p className="leading-relaxed">
                These Terms constitute the entire agreement between you and Seoro regarding the Service and supersede all 
                prior or contemporaneous understandings. The headings are for convenience only and have no legal effect.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">18. Contact Information</h2>
              <p className="leading-relaxed">
                For questions or notices about these Terms or the Service, email us at{" "}
                <a href="mailto:support@seoro.so" className="text-primary hover:underline">
                  support@seoro.so
                </a>{" "}
                or contact us through the support channels provided in your dashboard.
              </p>
            </section>

            <div className="mt-8 p-6 bg-muted/30 rounded-lg border border-border/50">
              <p className="text-foreground font-semibold">
                By using Seoro, you confirm that you have read, understood, and agree to these Terms of Use.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer onSignIn={handleSignIn} />
    </div>
  );
};

export default TermsOfService;


